import { firestore } from "firebase";

/**
 * Represents a billing catalog (Leistungsverzeichnis / LV) — a standardized
 * catalog of billing items used by insurance companies for cost estimation
 * in water-damage restoration projects.
 *
 * Dual-path storage (follows SystemConfiguration pattern):
 * - Group clients: `clientsAdministration/{adminClientId}/billingCatalogs/{catalogId}`
 * - Standalone clients: `clients/{clientId}/billingCatalogs/{catalogId}`
 *
 * One catalog per X360 `PriceClass`, which is the LV's de facto identity: X360 has
 * no LV entity, so an LV is simply the set of items sharing a price class. Document
 * ids are deterministic for idempotent imports: `{catalogKey}_{priceClass}`
 * (e.g. `lvm_LVMV2406`).
 *
 * Price variants of one LV are **not** split into separate catalogs. X360 holds a
 * single `AQGS2602` LV carrying both the standard and the Broenen prices, told apart
 * by the `#BRO` suffix on the article number, and that suffix is part of the article
 * number rather than something the import interprets.
 */
export interface BillingCatalog {
  /** Deterministic document ID */
  id: string;

  /**
   * Stable machine identifier of the LV's owner (e.g., "lvm", "oesv", "aqua").
   * The owner may be an insurer, a company, or the customer itself — an own LV
   * needs no fake insurer to be representable.
   */
  catalogKey: string;

  /** Display name of the LV's owner (e.g., "LVM", "öSV / Sparkasse", "Aqua (STLV)") */
  catalogName: string;

  /**
   * Haufe X360 tenant (the `company` login value) the prices were imported from,
   * e.g. "ABT". Null when the catalog did not come from X360.
   */
  tenant: string | null;

  /**
   * The issuer's own canonical LV id, where the issuer publishes one — öSV does
   * (`D/öSV/Dienstleister/WS/1.0/12.2024`). The X360 API carries no such field, so
   * this stays empty for imported catalogs. Never used for addressing; the document
   * id is the addressing contract.
   */
  lvIdentifier?: string | null;

  /** Original file name from import, or "LV API Import" for API-imported catalogs */
  fileName: string;

  /** The X360 `PriceClass` (e.g. "LVMV2406"), which is what identifies the LV */
  version: string;

  /** Date from which this LV is valid */
  validFrom: firestore.Timestamp | null;

  /** Date until this LV is valid (null = open-ended) */
  validUntil: firestore.Timestamp | null;

  /** Number of billing items in this LV */
  numberOfEntries: number;

  /** Branch number linking to Branch.branchNumber (for per-branch LVs like Aqua STLV), null for single-branch LVs */
  branchNumber: number | null;

  /** Upload/import timestamp */
  importedAt: firestore.Timestamp;

  /** User ID who imported this LV */
  importedBy: string;

  /** Catalog status */
  status: BillingCatalogStatus;

  /** How this catalog was imported */
  source?: BillingCatalogSource;

  /** Haufe X360 PriceClass code (e.g., "LVMV2406") — only for API-imported catalogs */
  priceClass?: string;
}

/**
 * Document id of the catalog holding items that carry no `PriceClass` and therefore
 * belong to no LV — in the ABT tenant, the six ERP posting placeholders (`ABSCHLAG`,
 * `GUTSCHRIFT`, `MAHN`, …), all in `ItemClass` `NBE`.
 *
 * Mirrors `NO_PRICE_CLASS_CATALOG_ID` in the IRES Cloud import
 * (`functions/src/services/billingCatalogs/BillingCatalogUtils.ts`). It is not an LV, so
 * it has no owner to name: render it with `getBillingCatalogDisplayName`.
 */
export const NO_PRICE_CLASS_CATALOG_ID = "_no_price_class";

/** Status of a billing catalog */
export type BillingCatalogStatus = "active" | "archived";

/** How a billing catalog was imported into the system */
export type BillingCatalogSource = "api" | "excel" | "seed";

/**
 * Represents a single billing item (Artikel / Position) within a billing catalog.
 * Stored as subcollection documents under `billingCatalogs/{catalogId}/billingItems`.
 */
export interface BillingItem {
  /** Firestore document ID */
  id: string;

  /**
   * Article/Position id, stored exactly as X360 delivers it, suffixes included
   * (e.g. "1.01.010", "TRO-03", "TRO-03#BRO", "0101000#24"). This is the value a
   * PDE's `billingItemId` must match, so it must never be rewritten on import.
   */
  articleId: string;

  /** Short description / headline */
  headline: string;

  /** Full description text */
  text: string;

  /** Unit of measure as delivered, casing included (e.g. "PAU", "M2", "STK", "STÜCK") */
  unit: string;

  /**
   * Unit price in EUR as delivered. `0` is a real value (217 positions carry it)
   * and means "priced at zero", which is not the same as `null` meaning the field
   * was absent. Do not treat `0` as missing.
   */
  price: number | null;

  /** Artikelklasse id for grouping, as delivered (e.g. "LVSLVM01BAU01BAU") */
  articleClass: string;

  /** IRES-added: limit value for this position */
  limit: number | null;

  /** IRES-added: qualifier/classification */
  qualifier: string | null;

  /** Reference to parent main position (öSV: "zugehörige Hauptleistung") */
  parentArticleId: string | null;
}

/**
 * Represents an article class (Artikelklasse) — a grouping category for billing items.
 * Stored as subcollection documents under `billingCatalogs/{catalogId}/articleClasses`,
 * keyed by `classId`.
 *
 * Ids come from X360 verbatim and carry no separators (`LVSLVM01BAU01BAU`). The
 * Excel files the insurers publish use a padded, dash-separated form of the same
 * code (`LVS-LVM-01BAU-01BAU`); the two do not convert mechanically.
 *
 * There is no sort order, because the X360 `ItemClass` entity has none. The ids are
 * numbered by design (`…01NOT`, `…02SA`, `…03LO`), so ascending `classId` is the
 * LV's own section order.
 */
export interface ArticleClass {
  /** Artikelklasse id as delivered (e.g. "LVSLVM01BAU01BAU") */
  classId: string;

  /** Description as delivered. Empty when X360 has none; render the id instead. */
  description: string;
}
